import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { resolvePreferredLeagues } from '../utils/leaguePreference';
import LeagueCircleSelector from '../components/LeagueCircleSelector';
import LoadingScreen from '../components/LoadingScreen';
import { ButtonPlaceholders, LeagueCirclePlaceholders } from '../components/LoadingPlaceholders';
import {
  getAugmentByValue,
  DEFAULT_ROUND_AUGMENTS,
  parseAugmentRef,
  getAugmentDisplaySvg,
  getAugmentDisplayName,
  getAugmentShortDesc
} from '../utils/augments';
import { groupAugmentStatsForUi, compareAugmentStatsBySuccess, getMatchAugmentStatTooltip, getRoundAugmentStatTooltip, MATCH_PLAY_SUCCESS_SECTION_TOOLTIP, ROUND_PLAY_SUCCESS_SECTION_TOOLTIP, formatAvgPlayerRoundPoints, countPlayersFromPointBuckets } from '../utils/bettingStats';
import { formatStandingRank, isRoundFinished } from '../utils/standingRankDisplay';
import { formatRoundDisplay } from '../utils/formatRoundDisplay';
import { useLiveGameSync } from '../hooks/useLiveGameSync';
import {
  mergeStandingsUpdated,
  formatProcessingMatchBanner
} from '../utils/mergeStandingsLiveEvent';
import { STANDINGS_UPDATED_TOAST_MESSAGE } from '../utils/standingsProcessingToast';
import {
  resolveSessionSelectedLeague,
  resolveSessionSelectedRoundType,
  selectedLeagueCodeStore,
  selectedRoundTypeStore,
  standingsByLeagueStore
} from '../utils/leaguePageCache';
import './Standings.css';
import './Betting.css';

function augmentMeta(code) {
  const { code: base } = parseAugmentRef(code);
  return getAugmentByValue(base) || DEFAULT_ROUND_AUGMENTS.find((a) => a.value === base);
}

function RoundAugmentsInline({ standing }) {
  const codes = Array.isArray(standing.appliedAugments)
    ? [...new Set(standing.appliedAugments.filter(Boolean))]
    : [];
  if (codes.length === 0) return null;
  return (
    <span className="standings-augments-inline">
        {codes.map((code) => {
          const meta = augmentMeta(code);
          const color = meta?.color || 'gold';
          const short = getAugmentDisplayName(code);
          const title = getAugmentShortDesc(code) || getAugmentDisplayName(code);
          const svg = getAugmentDisplaySvg(code) ?? meta?.svg;
          return (
            <span key={code} className={`standings-aug-chip standings-aug-chip--${color}`} title={title}>
              {svg ? (
                <img src={svg} alt={title} className="standings-aug-chip-img" />
              ) : (
                <span className="standings-aug-chip-text">{short}</span>
              )}
            </span>
          );
        })}
    </span>
  );
}

function formatPct(numerator, denominator) {
  if (!denominator) return '0%';
  return `${Math.round((numerator / denominator) * 100)}%`;
}

function RoundRecapAugmentList({ entries, statKind = 'match' }) {
  if (entries.length === 0) return null;
  return (
    <div className="standings-round-recap-aug-list">
      {entries.map(([code, counts]) => {
        const meta = augmentMeta(code);
        const svg = getAugmentDisplaySvg(code) ?? meta?.svg;
        const name = getAugmentDisplayName(code);
        const rateLabel = formatPct(counts.successful, counts.total);
        const title =
          statKind === 'round'
            ? getRoundAugmentStatTooltip(name, code, counts)
            : getMatchAugmentStatTooltip(name, counts);
        return (
          <span key={code} className="standings-round-recap-aug" title={title}>
            {svg ? (
              <img src={svg} alt={name} className="standings-round-recap-aug-img" />
            ) : (
              <span className="standings-round-recap-aug-text">{name}</span>
            )}
            <span className="standings-round-recap-aug-rate">{rateLabel}</span>
          </span>
        );
      })}
    </div>
  );
}

function RoundRecap({ roundStats, roundFinished }) {
  if (!roundStats || !roundStats.totalBets) return null;

  const matchAugmentEntries = Object.entries(groupAugmentStatsForUi(roundStats.augments))
    .filter(([, v]) => (v?.total || 0) > 0)
    .sort(([, a], [, b]) => compareAugmentStatsBySuccess(a, b));

  const roundAugmentEntries = roundFinished
    ? Object.entries(roundStats.roundAugments || {})
        .filter(([, v]) => (v?.total || 0) > 0)
        .sort(([, a], [, b]) => compareAugmentStatsBySuccess(a, b))
    : [];

  return (
    <div className="standings-round-recap">
      <h3 className="standings-round-recap-title">Round recap</h3>
      <div className="standings-round-recap-grid">
        <div className="standings-round-recap-stat">
          <span className="standings-round-recap-label">Players</span>
          <strong>{countPlayersFromPointBuckets(roundStats.pointBuckets)}</strong>
        </div>
        <div className="standings-round-recap-stat">
          <span className="standings-round-recap-label">Outcome hit</span>
          <strong>{formatPct(roundStats.correctOutcome, roundStats.totalBets)}</strong>
        </div>
        <div className="standings-round-recap-stat">
          <span className="standings-round-recap-label">Exact score</span>
          <strong>{formatPct(roundStats.correctResult, roundStats.totalBets)}</strong>
        </div>
        <div className="standings-round-recap-stat">
          <span className="standings-round-recap-label">Avg points</span>
          <strong>{formatAvgPlayerRoundPoints(roundStats.pointBuckets)}</strong>
        </div>
      </div>
      {matchAugmentEntries.length > 0 ? (
        <div className="standings-round-recap-augments">
          <span className="standings-round-recap-label" title={MATCH_PLAY_SUCCESS_SECTION_TOOLTIP}>
            Match play success
          </span>
          <RoundRecapAugmentList entries={matchAugmentEntries} statKind="match" />
        </div>
      ) : null}
      {roundAugmentEntries.length > 0 ? (
        <div className="standings-round-recap-augments standings-round-recap-augments--round">
          <span className="standings-round-recap-label" title={ROUND_PLAY_SUCCESS_SECTION_TOOLTIP}>
            Round play success
          </span>
          <RoundRecapAugmentList entries={roundAugmentEntries} statKind="round" />
        </div>
      ) : null}
    </div>
  );
}

function getRoundPayload(standingsData, roundType) {
  if (!standingsData) return null;
  if (roundType === 'previous') return standingsData.previousRound;
  if (roundType === 'current') return standingsData.currentRound;
  return standingsData.nextRound;
}

function getCoinLeagueForRoundType(standingsData, roundType) {
  if (!standingsData) return null;
  if (roundType === 'previous') return standingsData.previousRoundCoin;
  if (roundType === 'current') return standingsData.currentRoundCoin;
  return standingsData.nextRoundCoin;
}

function coinLeagueAcceptsJoin(coinLeague) {
  if (!coinLeague) return false;
  return !coinLeague.isMember && !coinLeague.roundHasStarted && coinLeague.canJoin !== false;
}

function coinLeagueJoinDisabled(coinLeague, joiningLeague) {
  const insufficientCoins =
    coinLeague.userBalance !== null &&
    coinLeague.userBalance !== undefined &&
    typeof coinLeague.userBalance === 'number' &&
    !Number.isNaN(coinLeague.userBalance) &&
    coinLeague.userBalance < coinLeague.entryFee &&
    !coinLeague.roundHasStarted;
  return (
    joiningLeague ||
    coinLeague.userBalance === null ||
    insufficientCoins ||
    coinLeague.ageRestriction ||
    !coinLeague.canJoin ||
    coinLeague.hasRoundBets === false
  );
}

function coinLeagueJoinDimmed(coinLeague) {
  return (
    coinLeague.userBalance === null ||
    coinLeague.ageRestriction ||
    !coinLeague.canJoin ||
    coinLeague.hasRoundBets === false
  );
}

function CoinLeagueJoinButton({
  coinLeague,
  joiningLeague,
  onJoinClick,
  className,
  joinLabel = 'Join',
  ...rest
}) {
  if (!coinLeagueAcceptsJoin(coinLeague)) return null;
  const disabled = coinLeagueJoinDisabled(coinLeague, joiningLeague);
  const dimmed = coinLeagueJoinDimmed(coinLeague);
  const feeLabel = `${coinLeague.entryFee} coin${coinLeague.entryFee === 1 ? '' : 's'}`;
  const ariaAction =
    joinLabel === 'Join Next Round' ? 'Join next round coin league' : 'Join coin league';
  return (
    <button
      type="button"
      className={className || 'standings-coin-join-btn'}
      onClick={() => {
        if (coinLeague.userBalance !== null) onJoinClick(coinLeague);
      }}
      disabled={disabled}
      style={{
        opacity: dimmed ? 0.5 : 1,
        cursor: dimmed ? 'not-allowed' : 'pointer'
      }}
      aria-label={
        joiningLeague ? 'Joining coin league' : `${ariaAction} for ${feeLabel}`
      }
      {...rest}
    >
      {joiningLeague ? (
        'Joining...'
      ) : (
        <>
          {joinLabel} (
          <span className="standings-coin-join-fee">
            {coinLeague.entryFee}
            <img src="/coin.svg" alt="" className="standings-coin-icon" aria-hidden="true" />
          </span>
          )
        </>
      )}
    </button>
  );
}

function nextRoundCoinCtaAvailable(standingsData) {
  const nextRoundCoin = standingsData?.nextRoundCoin;
  if (!standingsData?.nextRound?.round || !coinLeagueAcceptsJoin(nextRoundCoin)) {
    return false;
  }
  return Boolean(
    standingsData.currentRound?.round ||
    standingsData.previousRound?.round
  );
}

function resolveDefaultStandingsRoundType(standingsData) {
  if (!standingsData) return 'current';
  if (standingsData.currentRound?.round) return 'current';
  if (standingsData.previousRound?.round) return 'previous';
  if (standingsData.nextRound?.round) return 'next';
  return 'current';
}

function standingsRoundTypeAvailable(standingsData, roundType) {
  return !!getRoundPayload(standingsData, roundType)?.round;
}

function resolveStandingsRoundType(standingsData) {
  return resolveSessionSelectedRoundType(
    (type) => standingsRoundTypeAvailable(standingsData, type),
    resolveDefaultStandingsRoundType(standingsData)
  );
}

function shouldShowNextRoundCoinCta(standingsData, selectedRoundType, selectedCoinLeague) {
  if (!nextRoundCoinCtaAvailable(standingsData)) return false;
  if (selectedRoundType === 'current') {
    return selectedCoinLeague === standingsData.currentRoundCoin;
  }
  if (selectedRoundType === 'previous') {
    return selectedCoinLeague === standingsData.previousRoundCoin;
  }
  return false;
}

function resolveSelectedRoundPayload(standingsData, roundType) {
  const preferred = getRoundPayload(standingsData, roundType);
  if (preferred?.round) return preferred;
  if (standingsData.currentRound?.round) return standingsData.currentRound;
  if (standingsData.nextRound?.round) return standingsData.nextRound;
  if (standingsData.previousRound?.round) return standingsData.previousRound;
  return null;
}

function CoinLeagueSection({
  coinLeague,
  joiningLeague,
  onJoinClick,
  formatPlayerCell,
  nextRoundCoin,
  showNextRoundCoinCta = false
}) {
  if (!coinLeague) return null;

  const insufficientCoins =
    coinLeague.userBalance !== null &&
    coinLeague.userBalance !== undefined &&
    typeof coinLeague.userBalance === 'number' &&
    !Number.isNaN(coinLeague.userBalance) &&
    coinLeague.userBalance < coinLeague.entryFee &&
    !coinLeague.roundHasStarted;

  return (
    <div className="sc-card standings-section">
      <div className="standings-coin-header">
        <div className="standings-coin-header-title">
          <h2 className="standings-section-title">
            Round {formatRoundDisplay(coinLeague.round)} Coin League
          </h2>
          {coinLeague.isMember && (
            <span className="standings-badge standings-badge--joined">Joined</span>
          )}
        </div>
        <div className="standings-coin-header-actions">
          <CoinLeagueJoinButton
            coinLeague={coinLeague}
            joiningLeague={joiningLeague}
            onJoinClick={onJoinClick}
          />
          {((coinLeague.roundHasStarted && !coinLeague.isMember) ||
            (showNextRoundCoinCta && coinLeagueAcceptsJoin(nextRoundCoin))) && (
            <div className="standings-coin-closed-actions">
              {coinLeague.roundHasStarted && !coinLeague.isMember && (
                <span className="standings-message standings-message--warn">Round Started - No New Joins</span>
              )}
              {showNextRoundCoinCta && coinLeagueAcceptsJoin(nextRoundCoin) && (
                <CoinLeagueJoinButton
                  coinLeague={nextRoundCoin}
                  joiningLeague={joiningLeague}
                  onJoinClick={onJoinClick}
                  joinLabel="Join Next Round"
                  data-testid="standings-next-round-coin-cta"
                />
              )}
            </div>
          )}
        </div>
      </div>
      {!coinLeague.isMember && (
        <>
          {(coinLeague.userBalance === null || coinLeague.userBalance === undefined) && (
            <div className="sc-error standings-hint">Please log in to join this league.</div>
          )}
          {insufficientCoins && (
            <div className="sc-error standings-hint">
              Insufficient coins. You have {coinLeague.userBalance} coin(s), need {coinLeague.entryFee}.
            </div>
          )}
          {coinLeague.userBalance !== null && coinLeague.ageRestriction && (
            <div className="sc-error standings-hint">You must be at least 18 years old to join coin leagues.</div>
          )}
          {coinLeague.userBalance !== null &&
            !coinLeague.ageRestriction &&
            coinLeague.hasRoundBets === false &&
            !coinLeague.roundHasStarted && (
            <div className="sc-error standings-hint">
              Place your round bets before joining the coin league.
            </div>
          )}
        </>
      )}
      {coinLeague.standings.length > 0 ? (
        <table className="table" data-testid="standings-coin-table">
          <thead>
            <tr>
              <th>Player</th>
              <th>Pts</th>
            </tr>
          </thead>
          <tbody>
            {coinLeague.standings.map((standing) => (
              <tr key={standing.username}>
                <td>{formatPlayerCell(standing, {
                  showRoundAugments: true,
                  showMedals: isRoundFinished(coinLeague.roundInfo),
                })}</td>
                <td><strong>{standing.points}</strong></td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="standings-empty">No standings available yet.</p>
      )}
      <p className="standings-fee">Entry Fee: {coinLeague.entryFee} coin</p>
      {coinLeague.totalCoinsPrize > 0 && (
        <p className="standings-prize">
          Total Prize Pool: {coinLeague.totalCoinsPrize} coin
          {coinLeague.totalCoinsPrize !== 1 ? 's' : ''}
        </p>
      )}
      <p className="standings-prize-split">1st: 50% · 2nd: 30% · 3rd: 20%</p>
    </div>
  );
}

const Standings = () => {
  const { user, refreshUser, loading: authLoading } = useAuth();
  const preferredLeagues = useMemo(
    () => resolvePreferredLeagues(user?.lp, user?.ld),
    [user?.lp, user?.ld]
  );
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [standingsData, setStandingsData] = useState(null);
  /** leagueCode → last known standings (skip refetch when switching back if idle; session-lived) */
  const [standingsByLeague, setStandingsByLeagueState] = useState(() => ({
    ...standingsByLeagueStore.get()
  }));
  const standingsByLeagueRef = useRef(standingsByLeagueStore.get());
  const selectedLeagueRef = useRef(null);
  const setStandingsByLeague = useCallback((updater) => {
    setStandingsByLeagueState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      standingsByLeagueStore.set(next);
      standingsByLeagueRef.current = next;
      return next;
    });
  }, []);
  const [selectedRoundType, setSelectedRoundType] = useState(
    () => selectedRoundTypeStore.get() || 'current'
  );
  const selectRoundType = useCallback((type) => {
    selectedRoundTypeStore.set(type);
    setSelectedRoundType(type);
  }, []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [joiningLeague, setJoiningLeague] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [leagueToJoin, setLeagueToJoin] = useState(null);
  const [localProcessingMatch, setLocalProcessingMatch] = useState(null);
  const [updatedBanner, setUpdatedBanner] = useState(null);

  standingsByLeagueRef.current = standingsByLeague;
  selectedLeagueRef.current = selectedLeague;

  const processingMatch = localProcessingMatch || standingsData?.processingMatch || null;
  const processingBanner = formatProcessingMatchBanner(processingMatch);
  const statusBanner = processingBanner || updatedBanner;

  useEffect(() => {
    if (authLoading) return;
    if (preferredLeagues.length === 0) return;
    setSelectedLeague((current) => resolveSessionSelectedLeague(preferredLeagues, current));
  }, [preferredLeagues, authLoading]);

  useEffect(() => {
    setLocalProcessingMatch(null);
    setUpdatedBanner(null);
  }, [selectedLeague?.code]);

  useEffect(() => {
    if (!updatedBanner || processingBanner) return undefined;
    const timerId = window.setTimeout(() => setUpdatedBanner(null), 5000);
    return () => window.clearTimeout(timerId);
  }, [updatedBanner, processingBanner]);

  const applyStandingsPayload = useCallback((leagueCode, data) => {
    setStandingsByLeague((prev) => ({ ...prev, [leagueCode]: data }));
    if (selectedLeagueRef.current?.code === leagueCode) {
      setStandingsData(data);
    }
  }, []);

  const fetchStandings = useCallback(async ({ force = false } = {}) => {
    const league = selectedLeagueRef.current;
    if (!league) return;

    const leagueCode = league.code;
    if (!force) {
      const cached = standingsByLeagueRef.current[leagueCode];
      if (cached && !cached.processingMatch) {
        setStandingsData(cached);
        setLocalProcessingMatch(null);
        setSelectedRoundType(resolveStandingsRoundType(cached));
        setLoading(false);
        setError('');
        return;
      }
    }

    setLoading(true);
    setError('');
    try {
      const response = await api.get(`/standings/${leagueCode}`);
      const data = response.data;
      applyStandingsPayload(leagueCode, data);
      setSelectedRoundType(resolveStandingsRoundType(data));
      setLocalProcessingMatch(data?.processingMatch || null);
    } catch (err) {
      if (err.response?.status === 401) return;
      setError('Failed to load standings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [applyStandingsPayload]);

  useEffect(() => {
    if (selectedLeague) {
      fetchStandings();
    }
  }, [selectedLeague, fetchStandings]);

  const localProcessingMatchRef = useRef(localProcessingMatch);
  localProcessingMatchRef.current = localProcessingMatch;

  const handleLiveEvent = useCallback(
    (event) => {
      const league = selectedLeagueRef.current;
      if (!league) return;

      if (event?.type === 'STANDINGS_PROCESSING') {
        if (event.leagueCode && event.leagueCode !== league.code) return;
        if (!event.match) return;
        setUpdatedBanner(null);
        setLocalProcessingMatch(event.match);
        setStandingsData((prev) =>
          prev ? { ...prev, processingMatch: event.match } : prev
        );
        setStandingsByLeague((prev) => {
          const cur = prev[league.code];
          if (!cur) return prev;
          return { ...prev, [league.code]: { ...cur, processingMatch: event.match } };
        });
        return;
      }

      if (event?.type === 'STANDINGS_UPDATED') {
        if (event.leagueCode && event.leagueCode !== league.code) return;
        const cached = standingsByLeagueRef.current[league.code];
        setStandingsData((prev) => {
          const base = prev || cached;
          const merged = mergeStandingsUpdated(base, event);
          if (!merged) return prev;
          const pending = localProcessingMatchRef.current;
          if (
            pending?.gameId &&
            event.gameId &&
            String(pending.gameId) !== String(event.gameId)
          ) {
            merged.processingMatch = pending;
          } else {
            setLocalProcessingMatch(null);
            setUpdatedBanner(STANDINGS_UPDATED_TOAST_MESSAGE);
          }
          setStandingsByLeague((byLeague) => ({ ...byLeague, [league.code]: merged }));
          return merged;
        });
      }
    },
    []
  );

  const reconcileAfterSubscribe = useCallback(async () => {
    await fetchStandings({ force: true });
  }, [fetchStandings]);

  useLiveGameSync({
    games: [],
    onLiveEvent: handleLiveEvent,
    onVisibilityResume: () => fetchStandings({ force: true }),
    enabled: Boolean(selectedLeague),
    onSubscribed: reconcileAfterSubscribe
  });

  const handleJoinClick = (league) => {
    setLeagueToJoin(league);
    setShowConfirmDialog(true);
  };

  const handleConfirmJoin = async () => {
    if (!leagueToJoin) return;

    try {
      setJoiningLeague(true);
      setError('');
      await api.post(`/leagues/special/${leagueToJoin.roundCode}/join`);
      setShowConfirmDialog(false);
      setLeagueToJoin(null);
      await Promise.all([fetchStandings({ force: true }), refreshUser()]);
    } catch (err) {
      if (err.response?.status === 401) return;
      setError(err.response?.data?.error || 'Failed to join special league');
    } finally {
      setJoiningLeague(false);
    }
  };

  const handleCancelJoin = () => {
    setShowConfirmDialog(false);
    setLeagueToJoin(null);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatPlayerCell = (standing, { showRoundAugments = false, showMedals = false } = {}) => {
    const rankDisplay = formatStandingRank(standing.rank, { showMedals });
    const u = standing.username;
    return (
      <span
        className={`standings-player-line${showRoundAugments ? ' standings-player-line--with-augs' : ''}`}
      >
        <span className="standings-rank">{rankDisplay}</span>
        <Link to={`/profile/${encodeURIComponent(u)}`} className="standings-player-link">
          {u}
        </Link>
        {showRoundAugments ? <RoundAugmentsInline standing={standing} /> : null}
      </span>
    );
  };

  const selectedRoundPayload = standingsData
    ? resolveSelectedRoundPayload(standingsData, selectedRoundType)
    : null;
  const selectedCoinLeague = standingsData
    ? getCoinLeagueForRoundType(standingsData, selectedRoundType)
    : null;
  const showNextRoundCoinCta = standingsData
    ? shouldShowNextRoundCoinCta(standingsData, selectedRoundType, selectedCoinLeague)
    : false;
  const hasRoundNav = standingsData && (
    standingsData.previousRound?.round ||
    standingsData.currentRound?.round ||
    standingsData.nextRound?.round
  );

  if (!selectedLeague) {
    return (
      <div className="betting-page standings-page">
        <header className="betting-header">
          <h1 className="betting-title">Standings</h1>
          <p className="betting-subtitle">
            Global round & season leaderboards, plus the option to join coin leagues.
          </p>
          <LeagueCirclePlaceholders />
          <ButtonPlaceholders />
        </header>
        <LoadingScreen />
      </div>
    );
  }

  return (
    <div className="betting-page standings-page">
      <header className="betting-header">
        <h1 className="betting-title">Standings</h1>
        <p className="betting-subtitle">
          Global round & season leaderboards, plus the option to join coin leagues.
        </p>

        <div className="league-selector">
          <LeagueCircleSelector
            leagues={preferredLeagues}
            selectedLeague={selectedLeague}
            onSelect={(league) => {
              selectedLeagueCodeStore.set(league.code);
              setSelectedLeague(league);
              const cached = standingsByLeagueRef.current[league.code];
              if (cached && !cached.processingMatch) {
                setStandingsData(cached);
                setLocalProcessingMatch(null);
              } else {
                setStandingsData(null);
              }
            }}
          />
          {selectedLeague && (
            <span className="league-name">{selectedLeague.name}</span>
          )}
        </div>
      </header>

      {error && <div className="sc-error">{error}</div>}

      {statusBanner && (
        <div className="standings-processing-banner" role="status" data-testid="standings-processing-banner">
          {statusBanner}
        </div>
      )}

      {loading ? (
        <>
          <ButtonPlaceholders />
          <LoadingScreen />
        </>
      ) : standingsData ? (
        <>
          {hasRoundNav && (
            <div className="round-selector" data-testid="standings-round-selector">
              {standingsData.previousRound?.round && (
                <button
                  type="button"
                  className={`round-pill ${selectedRoundType === 'previous' ? 'round-pill--selected' : ''}`}
                  onClick={() => selectRoundType('previous')}
                >
                  ← Round {formatRoundDisplay(standingsData.previousRound.round.round)}
                </button>
              )}
              {standingsData.currentRound?.round && (
                <button
                  type="button"
                  className={`round-pill ${selectedRoundType === 'current' ? 'round-pill--selected' : ''}`}
                  onClick={() => selectRoundType('current')}
                >
                  Round {formatRoundDisplay(standingsData.currentRound.round.round)}
                </button>
              )}
              {standingsData.nextRound?.round && (
                <button
                  type="button"
                  className={`round-pill ${selectedRoundType === 'next' ? 'round-pill--selected' : ''}`}
                  onClick={() => selectRoundType('next')}
                >
                  Round {formatRoundDisplay(standingsData.nextRound.round.round)} →
                </button>
              )}
            </div>
          )}

          {selectedRoundPayload?.round && (
            <div className="sc-card standings-section">
              <h2 className="standings-section-title">
                Round {formatRoundDisplay(selectedRoundPayload.round.round)} Standings
              </h2>
              <RoundRecap
                roundStats={selectedRoundPayload.roundStats}
                roundFinished={isRoundFinished(selectedRoundPayload.round)}
              />
              {selectedRoundPayload.standings.length > 0 ? (
                <table className="table" data-testid="standings-table">
                  <thead>
                    <tr>
                      <th>Player</th>
                      <th>Pts</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedRoundPayload.standings.map((standing) => (
                      <tr key={standing.username}>
                        <td>{formatPlayerCell(standing, {
                          showRoundAugments: true,
                          showMedals: isRoundFinished(selectedRoundPayload.round),
                        })}</td>
                        <td><strong>{standing.points}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="standings-empty">No round standings available yet.</p>
              )}
            </div>
          )}

          <CoinLeagueSection
            coinLeague={selectedCoinLeague}
            joiningLeague={joiningLeague}
            onJoinClick={handleJoinClick}
            formatPlayerCell={formatPlayerCell}
            nextRoundCoin={standingsData.nextRoundCoin}
            showNextRoundCoinCta={showNextRoundCoinCta}
          />

          <div className="sc-card standings-section">
            <h2 className="standings-section-title">Season Standings</h2>
            {standingsData.season.standings.length > 0 ? (
              <table className="table table--season" data-testid="season-standings-table">
                <thead>
                  <tr>
                    <th>Player</th>
                    <th>Pts</th>
                    <th>Stack</th>
                  </tr>
                </thead>
                <tbody>
                  {standingsData.season.standings.map((standing) => (
                    <tr key={standing.username}>
                      <td>{formatPlayerCell(standing, { showMedals: false })}</td>
                      <td><strong>{standing.points}</strong></td>
                      <td>{standing.stack != null ? standing.stack : 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="standings-empty">No season standings available yet.</p>
            )}
          </div>

          <div className="betting-scroll-top">
            <button
              type="button"
              className="betting-scroll-top-btn"
              onClick={scrollToTop}
              aria-label="Scroll to top"
            >
              <svg
                className="betting-scroll-top-btn__icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="18 15 12 9 6 15" />
              </svg>
              <span className="betting-scroll-top-btn__label">Back to top</span>
            </button>
          </div>
        </>
      ) : (
        <p className="standings-empty">No standings available.</p>
      )}

      {showConfirmDialog && leagueToJoin && (
        <div className="standings-dialog-overlay">
          <div className="standings-dialog sc-card">
            <h3 className="standings-dialog-title">Confirm Join</h3>
            <p className="standings-dialog-text">
              Are you sure you want to join Round {formatRoundDisplay(leagueToJoin.round)} Coin League?
            </p>
            <p className="standings-dialog-charge">
              You will be charged {leagueToJoin.entryFee} coin.
            </p>
            {leagueToJoin.totalCoinsPrize > 0 && (
              <p className="standings-dialog-prize-split">
                Prize pool: {leagueToJoin.totalCoinsPrize} coin{leagueToJoin.totalCoinsPrize !== 1 ? 's' : ''} · 1st: 50% · 2nd: 30% · 3rd: 20%
              </p>
            )}
            <div className="standings-dialog-actions">
              <button
                type="button"
                className="sc-btn sc-btn-secondary"
                onClick={handleCancelJoin}
                disabled={joiningLeague}
              >
                Cancel
              </button>
              <button
                type="button"
                className="sc-btn sc-btn-primary"
                onClick={handleConfirmJoin}
                disabled={joiningLeague}
              >
                {joiningLeague ? 'Joining...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Standings;
